import { converter } from 'culori';

const toOklch = converter('oklch');

/**
 * Convert hex color to OKLCH format for CSS variables
 * Tailwind CSS 4 uses OKLCH format: "lightness chroma hue"
 * Uses culori library for accurate perceptual color space conversion
 * 
 * IMPORTANT: Safari requires lightness as percentage (71%) not decimal (0.71)
 * See: https://github.com/saadeghi/daisyui/issues/2703
 * 
 * @param hex - Hex color string (e.g., "#7AB51D")
 * @returns OKLCH string with percentage lightness (e.g., "71% 0.18 130")
 */
export function hexToOklch(hex: string): string {
  // Convert hex to OKLCH using culori
  const oklchColor = toOklch(hex);
  
  if (!oklchColor) {
    throw new Error(`Failed to convert ${hex} to OKLCH`);
  }
  
  // Format as "L% C H" - Safari requires percentage for lightness!
  const l = Math.round((oklchColor.l ?? 0) * 100);
  const c = (oklchColor.c ?? 0).toFixed(2);
  const h = (oklchColor.h ?? 0).toFixed(0);
  
  return `${l}% ${c} ${h}`;
}

/**
 * Convert hex color to desaturated OKLCH for subtle backgrounds
 * Reduces chroma to create a muted, toned-down version
 * @param hex - Hex color string (e.g., "#7AB51D")
 * @param targetChroma - Target chroma value (default: 0.03 for very subtle)
 * @returns OKLCH string with reduced chroma (e.g., "92% 0.03 130")
 */
export function hexToDesaturatedOklch(hex: string, targetChroma: number = 0.03): string {
  // Convert hex to OKLCH using culori
  const oklchColor = toOklch(hex);
  
  if (!oklchColor) {
    throw new Error(`Failed to convert ${hex} to OKLCH`);
  }
  
  // Use high lightness (92% for light backgrounds) and low chroma for subtle color
  const l = 92; // Light background
  const c = targetChroma.toFixed(2); // Very low chroma = desaturated
  const h = (oklchColor.h ?? 0).toFixed(0); // Keep original hue
  
  return `${l}% ${c} ${h}`;
}

/**
 * Validate hex color format
 * @param hex - Hex color string
 * @returns true if valid hex color
 */
export function isValidHexColor(hex: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

/**
 * Get contrasting text color (black or white) for a given background color
 * @param hex - Background hex color
 * @returns "#000000" or "#FFFFFF"
 */
export function getContrastColor(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calculate relative luminance (WCAG formula)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black for light backgrounds, white for dark backgrounds
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}
